import React, { useState } from "react";
import { Check, Undo2, ChevronDown, AlertTriangle } from "lucide-react";
import { PetAvatar } from "@/components/PetAvatar";
import { CARE_TYPES } from "@/lib/constants";
import { fmtTime, clock, fmtAgo } from "@/lib/api";

export function TaskCard({ task, onDone, onUndo, busy, showDetails = false, idx }) {
  const [open, setOpen] = useState(showDetails);
  const t = CARE_TYPES[task.type] || CARE_TYPES.other;
  const Icon = t.icon;
  const done = task.done;
  const tid = `task-${idx}`;
  return (
    <div data-testid={tid} className={`pl-card p-3 sm:p-4 transition-colors ${done ? "bg-[#F3FBF7] border-[#BBF0D9]" : task.overdue ? "border-[#F3C3B5]" : ""}`}>
      <div className="flex items-center gap-3">
        <PetAvatar pet={{ name: task.pet_name, photo_url: task.pet_photo }} size={52} />
        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen(!open)} data-testid={`${tid}-expand`}>
          <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: t.color }}>
            <Icon className="h-3.5 w-3.5" /> {task.pet_name} · {fmtTime(task.time)}
            {task.overdue && !done && <span className="pl-chip bg-[#FDF2F0] text-[#C2410C] !py-0 !px-2"><AlertTriangle className="h-3 w-3" />Late</span>}
          </div>
          <p className={`font-heading font-semibold text-[17px] leading-snug truncate ${done ? "text-slate-500" : ""}`}>{task.title}</p>
          {task.amount && <p className="text-sm text-slate-500 truncate">{task.amount}</p>}
        </button>
        {done ? (
          <div className="flex flex-col items-end gap-1">
            <span data-testid={`${tid}-done-badge`} className="h-14 w-14 rounded-full bg-[#10B981] text-white grid place-items-center animate-pop"><Check className="h-7 w-7" strokeWidth={3} /></span>
          </div>
        ) : (
          <button data-testid={`${tid}-done-btn`} disabled={busy} onClick={() => onDone(task)}
            className="h-14 w-14 rounded-full border-[3px] border-dashed border-[#F59E0B] bg-[#FFF7E6] grid place-items-center active:scale-90 transition-transform disabled:opacity-50">
            <Check className="h-7 w-7 text-[#F59E0B]" strokeWidth={3} />
          </button>
        )}
      </div>
      {done && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl bg-white/80 px-3 py-2 text-sm">
          <span data-testid={`${tid}-done-by`} className="text-[#047857] font-semibold">
            Done by {done.by}{done.via === "sitter" ? " (sitter)" : ""} · {clock(done.at)} <span className="font-normal text-slate-500">({fmtAgo(done.at)})</span>
          </span>
          {onUndo && <button data-testid={`${tid}-undo-btn`} onClick={() => onUndo(task)} className="text-slate-500 flex items-center gap-1 text-xs font-semibold"><Undo2 className="h-3.5 w-3.5" />Undo</button>}
        </div>
      )}
      {open && task.instructions && (
        <p className="mt-2 text-sm text-slate-600 bg-[#FAF7F2] rounded-xl px-3 py-2 whitespace-pre-line">{task.instructions}</p>
      )}
      {!open && task.instructions && (
        <button onClick={() => setOpen(true)} className="mt-1 text-xs text-slate-400 flex items-center gap-0.5"><ChevronDown className="h-3 w-3" />Instructions</button>
      )}
    </div>
  );
}
